import { Controller } from "@hotwired/stimulus"

// "Budget & Zeitraum": the board's single estimate editor. Every site and task header carries its
// current values ([data-planning-estimate-trigger], see Planning::EstimateTrigger); clicking one
// fills this form from them and pins the panel under that header. The click is delegated because
// the headers are re-rendered by Turbo streams after every mutation.
//
// The form is an ordinary Turbo form, so the request, its field names and the 422 flash are the
// ones the per-row editors had; a successful submit closes the panel.
const TRIGGER = "[data-planning-estimate-trigger]"

export default class extends Controller {
  static targets = [
    "panel",
    "form",
    "plannableType",
    "plannableId",
    "effort",
    "unit",
    "dates",
    "multiSpanHint",
    "startDate",
    "endDate",
    "weekday",
  ]

  connect() {
    this.trigger = null
    this.boundClick = this.onDocumentClick.bind(this)
    this.boundTriggerKey = this.onTriggerKeydown.bind(this)
    this.boundKeydown = this.onKeydown.bind(this)
    this.boundReposition = this.reposition.bind(this)
    // Capture phase: board controls that stopPropagation() (map pin, panel tabs) must still close it.
    document.addEventListener("click", this.boundClick, true)
    document.addEventListener("keydown", this.boundTriggerKey)
  }

  disconnect() {
    document.removeEventListener("click", this.boundClick, true)
    document.removeEventListener("keydown", this.boundTriggerKey)
    this.removeOpenListeners()
    clearTimeout(this.focusTimeout)
  }

  onDocumentClick(event) {
    const trigger = event.target.closest(TRIGGER)
    if (trigger) {
      event.preventDefault()
      this.open(trigger)
    } else if (this.isOpen() && !this.panelTarget.contains(event.target)) {
      this.close()
    }
  }

  // The headers are role="button": Enter and Space open the editor like a click.
  onTriggerKeydown(event) {
    if (event.key !== "Enter" && event.key !== " ") return
    const trigger = event.target.closest?.(TRIGGER)
    if (!trigger || trigger !== event.target) return
    event.preventDefault()
    this.open(trigger)
  }

  open(trigger) {
    this.trigger = trigger
    this.fill(trigger.dataset)
    this.panelTarget.classList.remove("hidden")
    this.position()
    this.removeOpenListeners()
    document.addEventListener("keydown", this.boundKeydown)
    window.addEventListener("resize", this.boundReposition)
    window.addEventListener("scroll", this.boundReposition, true)
    this.effortTarget.focus()
  }

  fill(data) {
    this.plannableTypeTarget.value = data.plannableType
    this.plannableIdTarget.value = data.plannableId
    this.effortTarget.value = data.plannedEffort || ""
    this.unitTarget.value = data.plannedEffortUnit || "person_days"
    // The date fields sync their visible input from the hidden one on `change`.
    this.setDate(this.startDateTarget, data.startDate)
    this.setDate(this.endDateTarget, data.endDate)
    const days = this.parseDays(data.workingDays)
    this.weekdayTargets.forEach((box) => {
      box.checked = days.includes(Number(box.value))
    })
    // A task worked in several periods: a disabled fieldset submits no dates.
    const multiSpan = data.multiSpan === "true"
    this.datesTarget.disabled = multiSpan
    this.multiSpanHintTarget.classList.toggle("hidden", !multiSpan)
  }

  close({ deferFocus = false } = {}) {
    if (!this.isOpen()) return
    this.panelTarget.classList.add("hidden")
    this.removeOpenListeners()
    // After a save the stream replaces the header on the next repaint; focus its replacement.
    if (deferFocus) this.focusTimeout = setTimeout(() => this.restoreFocus(), 100)
    else this.restoreFocus()
  }

  // A successful save re-renders the header (and with it the trigger), so the panel closes; on a
  // 422 it stays open with the planner's input and the server's flash.
  submitted(event) {
    if (event.detail?.success) this.close({ deferFocus: true })
  }

  onKeydown(event) {
    if (event.key !== "Escape") return
    if (event.defaultPrevented || this.datePopoverOpen()) return
    this.close()
  }

  // ESC inside an open calendar closes the calendar, not the editor.
  datePopoverOpen() {
    try {
      return Boolean(this.panelTarget.querySelector(":popover-open"))
    } catch {
      return false
    }
  }

  isOpen() {
    return !this.panelTarget.classList.contains("hidden")
  }

  removeOpenListeners() {
    document.removeEventListener("keydown", this.boundKeydown)
    window.removeEventListener("resize", this.boundReposition)
    window.removeEventListener("scroll", this.boundReposition, true)
  }

  reposition() {
    if (this.isOpen()) this.position()
  }

  // Pin the panel below the trigger, clamped to the viewport; flip above if it would overflow.
  position() {
    const anchor = this.currentTrigger()
    if (!anchor) return
    const rect = anchor.getBoundingClientRect()
    const panel = this.panelTarget
    const width = panel.offsetWidth || 320
    const left = Math.max(8, Math.min(rect.left, window.innerWidth - width - 8))
    let top = rect.bottom + 4
    if (top + panel.offsetHeight > window.innerHeight - 8) {
      top = Math.max(8, rect.top - panel.offsetHeight - 4)
    }
    panel.style.left = `${left}px`
    panel.style.top = `${top}px`
  }

  // The header a save re-rendered is a new element; find it by its plannable.
  currentTrigger() {
    if (this.trigger?.isConnected) return this.trigger
    const type = this.plannableTypeTarget.value
    const id = this.plannableIdTarget.value
    this.trigger = document.querySelector(
      `${TRIGGER}[data-plannable-type="${CSS.escape(type)}"][data-plannable-id="${CSS.escape(id)}"]`,
    )
    return this.trigger
  }

  restoreFocus() {
    this.currentTrigger()?.focus()
  }

  setDate(input, value) {
    input.value = value || ""
    input.dispatchEvent(new Event("change", { bubbles: true }))
  }

  parseDays(raw) {
    try {
      const days = JSON.parse(raw || "[]")
      return Array.isArray(days) && days.length ? days : [1, 2, 3, 4, 5]
    } catch {
      return [1, 2, 3, 4, 5]
    }
  }
}
